/**
 * THE ONLY MODULE IN THE PRODUCT THAT IMPORTS THE CHARTING LIBRARY.
 *
 * G2 test b: "Static check: zero direct imports of the charting library outside the
 * wrapper module." `bata/no-charting-library-import` in eslint.config.js enforces it,
 * with `src/charts/` as the only permitted path. `tools/lint-fixtures/echarts-import.ts`
 * proves the rule fires.
 *
 * Why the boundary is worth the indirection: ECharts will happily render every form
 * G2 §2.5 bans. Its option object has `smooth`, it has a `yAxis` array, it has
 * `type: 'gauge'` and `type: 'pie'`. If feature code could reach that object, every
 * one of those becomes one keystroke away and the rules become a review checklist.
 * Here, the ECharts option is a private local variable constructed from a `ChartSpec`
 * that cannot describe a violation — and this file never writes the banned keys.
 *
 * The imports are explicit rather than the `echarts` barrel, so the bundle carries
 * only sanctioned chart types. `PieChart`, `GaugeChart` and `RadarChart` are not
 * imported, which means the banned forms are not merely unreachable — they are
 * not in the shipped JavaScript at all.
 */

import * as echarts from 'echarts/core';
import { BarChart, CustomChart, HeatmapChart, LineChart, ScatterChart } from 'echarts/charts';
import {
  DatasetComponent, GridComponent, LegendComponent, MarkAreaComponent,
  MarkLineComponent, TooltipComponent, VisualMapComponent,
} from 'echarts/components';
import { CanvasRenderer, SVGRenderer } from 'echarts/renderers';

import { isMissing, type ChartSpec, type Datum, type NumericAxis, type ReferenceMark, type Series } from './types';
import { axisScale, legendPolicy } from './validate';
import { markerSymbol, slotForSeries, type ChartTheme } from './theme';
import { motionMs } from '../primitives/motion';
import { categoryWords, isDateLabel } from './category-words';

echarts.use([
  LineChart, BarChart, ScatterChart, HeatmapChart, CustomChart,
  GridComponent, TooltipComponent, LegendComponent, DatasetComponent,
  VisualMapComponent, MarkAreaComponent, MarkLineComponent,
  CanvasRenderer, SVGRenderer,
]);

export type EChartsInstance = echarts.ECharts;

/** Translation is injected — this module holds no strings (G6). */
export type Translate = (key: string, vars?: Record<string, unknown>) => string;

/** A series' display name: its key, with the entity it names when it names one. */
export const seriesName = (t: Translate, s: Series): string => t(s.nameKey, s.nameValues ?? {});

/**
 * A per-mark tooltip that reads as a sentence — `ChartSpec.tooltipKey`.
 *
 * The catalogue places the arguments (G6 test b), so nothing is concatenated here. A missing
 * value keeps saying which kind of nothing it is, as the default formatter does. The result
 * is plain text: ECharts renders a string formatter as HTML, so the one character that could
 * break out of it is escaped rather than trusted.
 */
function speakingTooltip(key: string, ctx: BuildContext) {
  const escape = (text: string): string => text.replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);
  return (raw: unknown): string => {
    const p = (Array.isArray(raw) ? raw[0] : raw) as { name?: unknown; value?: unknown; seriesName?: unknown } | undefined;
    const v = Array.isArray(p?.value) ? p?.value[p.value.length - 1] : p?.value;
    const value = v === null || v === undefined || Number.isNaN(Number(v))
      ? ctx.t('chart.missing.noObservation')
      : ctx.formatNumber(Number(v));
    // On a numeric x axis a point's "category" is its x, formatted, and its name — when the
    // series names its points (ADR 9050) — is what the point IS: `{point}`.
    const pair = Array.isArray(p?.value) && p.value.length === 2;
    const x = pair ? Number((p.value as unknown[])[0]) : Number.NaN;
    const name = p?.name !== undefined && p.name !== '' ? String(p.name) : '';
    const category = pair ? (Number.isNaN(x) ? '' : ctx.formatNumber(x)) : categoryWords(name, ctx.locale);
    const point = pair ? name : '';
    return escape(ctx.t(key, { category, value, point, series: String(p?.seriesName ?? '') }));
  };
}

/**
 * The default per-mark tooltip, for a chart whose categories are dates: ECharts' own item tooltip
 * prints the declared category (`2026-09-28`), so this prints the same three things — the category,
 * the series and the value — with the category as a date (audit 2, G). The value stays on the same
 * line, apart from the series name by a space and set in bold.
 */
function datedItemTooltip(ctx: BuildContext) {
  const escape = (text: string): string => text.replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);
  return (raw: unknown): string => {
    const p = (Array.isArray(raw) ? raw[0] : raw) as { name?: unknown; value?: unknown; seriesName?: unknown; marker?: unknown } | undefined;
    const v = Array.isArray(p?.value) ? p?.value[p.value.length - 1] : p?.value;
    const value = v === null || v === undefined || Number.isNaN(Number(v))
      ? ctx.t('chart.missing.noObservation')
      : ctx.formatNumber(Number(v));
    const marker = typeof p?.marker === 'string' ? p.marker : '';
    return `${escape(categoryWords(String(p?.name ?? ''), ctx.locale))}<br/>${marker}${escape(String(p?.seriesName ?? ''))} <b>${escape(value)}</b>`;
  };
}

/** Locale-aware number formatting for axis ticks and tooltips (G6). */
export type FormatNumber = (value: number) => string;

export interface BuildContext {
  readonly theme: ChartTheme;
  readonly t: Translate;
  readonly formatNumber: FormatNumber;
  readonly locale: string;
}

/*
 * The ECharts option type is a union of some two hundred members whose narrowing cost at
 * every assignment site is not repayable. The `any` is confined to this one alias, inside
 * the one module permitted to know ECharts exists, and it never leaves the file:
 * `buildOption`'s result is consumed only by `applyOption`, and feature code cannot obtain
 * the type at all. That containment is what makes it acceptable here and nowhere else.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EChartsOption = Record<string, any>;

/**
 * reqs/00 D8 / G2 §2.5 — "A gap in a series is a gap, not a joined line and not a
 * zero."
 *
 * `null` is how ECharts represents an absent observation, and `connectNulls` is left
 * at its default of `false` so the line BREAKS rather than bridging the gap. A zero
 * would be a lie about a counted zero; a joined line would be a lie about the shape
 * of the series between two observations that do not exist.
 */
const toEchartsValue = (d: Datum): number | null => (isMissing(d) ? null : d);

function maxAbsolute(series: readonly Series[]): number {
  let max = 0;
  for (const s of series) {
    for (const d of s.data) {
      if (!isMissing(d)) max = Math.max(max, Math.abs(d));
    }
  }
  return max;
}

function categoriesOf(spec: ChartSpec): readonly string[] {
  const axis = spec.categoryAxis;
  if (axis.kind === 'numeric') return axis.values.map(String);
  // design/09 §12.11 — the DECLARED order, verbatim. Nothing here sorts, which is
  // the only way "EU 40 before EU 41, never 10, 11, 9" is guaranteed rather than
  // hoped for. `validate.ts` has already asserted every series aligns to this list.
  if (axis.kind === 'size-scale' || axis.kind === 'ordinal') return axis.order;
  const longest = spec.series.reduce((n, s) => Math.max(n, s.data.length), 0);
  return Array.from({ length: longest }, (_, i) => String(i + 1));
}

function textStyle(ctx: BuildContext, size: number, colour: string, numeric = false) {
  return {
    color: colour,
    fontFamily: numeric ? ctx.theme.font.numeric : ctx.theme.font.ui,
    fontSize: size,
  };
}

/**
 * Where an axis NAME goes, so it is never cut.
 *
 * Both names were at `nameLocation: 'end'`. For the category axis that is past the right
 * end of the x-axis, inside a 24px `grid.right`; for the value axis it is centred on the
 * top of the y-axis, half of it past the left edge of the canvas. `containLabel` makes room
 * for tick LABELS and not for axis NAMES, so neither margin grew to fit — audit v4 G-12 read
 * "Si" for Size, "W" for Week and "Dema" for Demand.
 *
 * - x (category): centred under the tick labels. `grid.bottom` below gains the room.
 * - y (value, and a heatmap's rows): at the top of the axis, but LEFT-aligned on it, so the
 *   name runs inwards over the plot instead of outwards off the canvas. `grid.top` gains the
 *   room.
 */
const X_AXIS_NAME = { nameLocation: 'middle' as const, nameGap: 28 };
const Y_AXIS_NAME = { nameLocation: 'end' as const, nameGap: 12 };
/** The extra room the names above take from each edge, in px. */
const NAME_ROOM = { top: 16, bottom: 20 };

/**
 * §2.5 — "Maximum three digits on value-axis labels — use k / m and say so in the
 * unit." The magnitude goes into the axis NAME, so the ticks stay three digits and
 * the reader is still told what they mean.
 */
function valueAxisFor(spec: ChartSpec, ctx: BuildContext) {
  // A stated range (two charts on one scale, ADR 9038 R) also fixes the magnitude, so both
  // charts' ticks say the same «k» rather than one in units and one in thousands.
  const range = spec.valueAxis.sharedRange;
  const extent = range === undefined ? maxAbsolute(spec.series) : Math.max(Math.abs(range.min), Math.abs(range.max));
  const { divisor, magnitudeKey } = axisScale(extent);
  const unit = ctx.t(spec.valueAxis.unitKey);
  const magnitude = divisor === 1 ? '' : ctx.t(magnitudeKey);
  return {
    type: 'value' as const,
    name: magnitude ? ctx.t('chart.axis.nameWithMagnitude', { unit, magnitude }) : unit,
    ...Y_AXIS_NAME,
    nameTextStyle: { ...textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.axisLabel), align: 'left' as const },
    // A bar form has already been asserted zeroBased in validate.ts.
    scale: !spec.valueAxis.zeroBased,
    // validate.ts has asserted every datum lies inside it.
    ...(range === undefined ? {} : { min: range.min, max: range.max }),
    axisLabel: {
      ...textStyle(ctx, ctx.theme.fontSize.axis, ctx.theme.ink.axisLabel, true),
      formatter: (value: number) => ctx.formatNumber(value / divisor),
    },
    // §7.4 — recessive hairline grid and axis.
    splitLine: { lineStyle: { color: ctx.theme.structure.grid, width: 1 } },
    axisLine: { lineStyle: { color: ctx.theme.structure.axis, width: 1 } },
    axisTick: { show: false },
  };
}

/**
 * A NUMERIC x axis (ADR 9013's chart extension): points at their true x. Ticks scaled to three
 * digits with the magnitude in the axis name and formatted by the locale-aware formatter — the
 * value axis's rules, applied to the other axis. Still one x axis and one value axis.
 */
function numericAxisFor(axis: NumericAxis, ctx: BuildContext) {
  const { divisor, magnitudeKey } = axisScale(Math.max(0, ...axis.values.map(Math.abs)));
  const unit = ctx.t(axis.unitKey);
  const label = ctx.t(axis.labelKey);
  const named = ctx.t('chart.axis.nameWithUnit', { label, unit });
  // ADR 9050 — a ratio scale, one tick per doubling. `validate.ts` has asserted every x > 0.
  const log = axis.scale === 'log';
  return {
    ...(log ? { type: 'log' as const, logBase: 2 } : { type: 'value' as const, scale: !axis.zeroBased }),
    name: divisor === 1 ? named : ctx.t('chart.axis.nameWithMagnitude', { unit: named, magnitude: ctx.t(magnitudeKey) }),
    ...X_AXIS_NAME,
    nameTextStyle: textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.axisLabel),
    axisLabel: {
      ...textStyle(ctx, ctx.theme.fontSize.axis, ctx.theme.ink.axisLabel, true),
      formatter: (value: number) => ctx.formatNumber(value / divisor),
    },
    axisPointer: { label: { formatter: (p: { value: unknown }) => ctx.formatNumber(Number(p.value)) } },
    axisLine: { lineStyle: { color: ctx.theme.structure.axis, width: 1 } },
    axisTick: { show: false },
    splitLine: { show: false },
  };
}

/**
 * Reference marks on a numeric-x chart, drawn exactly as §7.4 draws an event line: a recessive
 * dashed hairline in the grid token, its label in the annotation ink, `silent` so it never
 * steals a tooltip. `validate.ts` has asserted every position is on its axis.
 */
type RuleMark = Exclude<ReferenceMark, { kind: 'path' }>;

function referenceMarks(
  declared: readonly ReferenceMark[] | undefined, axis: NumericAxis, spec: ChartSpec, ctx: BuildContext,
): EChartsOption | undefined {
  // A trend path is drawn as its own line, not as a rule (`trendSeries`).
  const references = declared?.filter((mark): mark is RuleMark => mark.kind !== 'path');
  if (references === undefined || references.length === 0) return undefined;
  const ys = spec.series.flatMap((s) => s.data.filter((d): d is number => !isMissing(d)));
  const lo = Math.min(...axis.values, ...ys);
  const hi = Math.max(...axis.values, ...ys);
  const text = (mark: RuleMark): string => ctx.t(mark.labelKey, mark.labelValues ?? {});
  /*
   * WHERE A LABEL GOES, so it never sits on the axis name or on another label (ui-c,
   * 2026-09-26: a 50% marker at a low rank printed its label over "% of residual units").
   * ECharts put every label at `end` — above the plot for a vertical line, which is exactly
   * the band the value axis's name occupies, and centred on the line, so two marks close
   * together overprinted. Pixel widths are not known when the option is built, so the rule is
   * one that holds at any width rather than a measurement:
   *
   *   x marks  inside the plot, below its top edge (the axis name is above it), on their own
   *            ROW each — sorted by position, row k is k label-heights down, so no two x
   *            labels can share a line however close their marks are. A mark in the right
   *            third reads leftwards from its line, so it is not cut at the plot's edge.
   *   y marks  inside the plot, just above their line, alternating start / end / middle, so
   *            two thresholds close in value do not share a corner, and none meets the series'
   *            end label outside the plot on the right.
   *
   * Every label carries the chart surface behind it, so a label that crosses the series stays
   * readable in both themes.
   */
  const ROW = ctx.theme.fontSize.label + 6;
  const [xMin, xMax] = [Math.min(...axis.values), Math.max(...axis.values)];
  const span = xMax - xMin;
  const xOrder = references
    .filter((mark): mark is Extract<RuleMark, { kind: 'x' }> => mark.kind === 'x')
    .sort((a, b) => a.at - b.at);
  const yOrder = references.filter((mark) => mark.kind === 'y');
  const Y_POSITIONS = ['insideStartTop', 'insideEndTop', 'insideMiddleTop'] as const;
  const plate = { backgroundColor: ctx.theme.surface, padding: [1, 3] };
  const labelFor = (mark: RuleMark) => {
    if (mark.kind === 'x') {
      const row = xOrder.indexOf(mark);
      const leftwards = span > 0 && (mark.at - xMin) / span > 2 / 3;
      return {
        formatter: text(mark),
        position: 'end',
        rotate: 0,
        align: leftwards ? 'right' : 'left',
        verticalAlign: 'top',
        // Inside the plot: down from its top edge by one gap plus the row.
        offset: [leftwards ? -4 : 4, 6 + row * ROW],
        ...plate,
      };
    }
    if (mark.kind === 'y') {
      return { formatter: text(mark), position: Y_POSITIONS[yOrder.indexOf(mark) % Y_POSITIONS.length], ...plate };
    }
    return { formatter: text(mark), position: 'end', ...plate };
  };
  return {
    silent: true,
    symbol: ['none', 'none'],
    animation: false,
    lineStyle: { color: ctx.theme.structure.grid, width: 1, type: 'dashed' },
    emphasis: { disabled: true },
    label: { show: true, position: 'end', ...textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.annotation) },
    data: references.map((mark) => {
      if (mark.kind === 'identity') {
        // y = x across the range both axes share on screen.
        return [{ coord: [lo, lo], label: labelFor(mark) }, { coord: [hi, hi] }];
      }
      return mark.kind === 'x'
        ? { xAxis: mark.at, label: labelFor(mark) }
        : { yAxis: mark.at, label: labelFor(mark) };
    }),
  };
}

/**
 * ADR 9050 — a scatter's trend path, as a line of its own under the dots: straight segments
 * between the vertices the caller computed (never `smooth`), in the title ink, solid, named at
 * its end. `silent`: it has no tooltip and no legend entry, because it is a summary of the
 * points rather than a series of observations.
 */
function trendSeries(spec: ChartSpec, ctx: BuildContext): readonly EChartsOption[] {
  if (spec.form !== 'scatter' || spec.references === undefined) return [];
  return spec.references.flatMap((mark) => (mark.kind !== 'path' ? [] : [{
    id: `trend:${mark.labelKey}`,
    type: 'line',
    data: mark.points.map((point) => [point.x, point.y]),
    silent: true,
    symbol: 'none',
    z: 1,
    connectNulls: false,
    lineStyle: { color: ctx.theme.ink.title, width: ctx.theme.structure.lineWidth },
    itemStyle: { color: ctx.theme.ink.title },
    emphasis: { disabled: true },
    tooltip: { show: false },
    endLabel: {
      show: true,
      ...textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.annotation),
      formatter: ctx.t(mark.labelKey, mark.labelValues ?? {}),
      backgroundColor: ctx.theme.surface,
      padding: [1, 3],
    },
    label: { show: false },
  }]));
}

/**
 * ADR 9051 — a scatter's interval: one whisker per category from the lower bound's series to
 * the upper bound's, a straight segment in the title ink under the dots, drawn only where BOTH
 * bounds are present (a half interval is not drawn as a whole one). `silent`, with no legend
 * entry and no tooltip: the bounds are series of their own, named in the legend and read in
 * the table view, and the whisker only joins them.
 */
function intervalSeries(spec: ChartSpec, ctx: BuildContext): readonly EChartsOption[] {
  if (spec.form !== 'scatter' || spec.interval === undefined) return [];
  const { low, high } = spec.interval;
  const lower = spec.series.find((s) => s.id === low);
  const upper = spec.series.find((s) => s.id === high);
  if (lower === undefined || upper === undefined) return [];
  const data = lower.data.flatMap((d, i) => {
    const u = upper.data[i];
    return isMissing(d) || u === undefined || isMissing(u) ? [] : [[i, d, u]];
  });
  return [{
    id: `interval:${low}:${high}`,
    type: 'custom',
    data,
    silent: true,
    z: 1,
    tooltip: { show: false },
    emphasis: { disabled: true },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    renderItem: (_params: unknown, api: any) => {
      const from = api.coord([api.value(0), api.value(1)]) as [number, number];
      const to = api.coord([api.value(0), api.value(2)]) as [number, number];
      return {
        type: 'line',
        shape: { x1: from[0], y1: from[1], x2: to[0], y2: to[1] },
        style: { stroke: ctx.theme.ink.title, lineWidth: ctx.theme.structure.lineWidth },
      };
    },
  }];
}

/**
 * ADR 9050 — one point as ECharts takes it: its x (the series' own, else the axis's), its y,
 * and, when the series says so, its name (the tooltip's `{point}`), a direct label beside it,
 * and a hollow mark — an outline in the series colour on the chart surface.
 */
function pointOf(s: Series, i: number, d: Datum, numeric: NumericAxis | null, colour: string, ctx: BuildContext): unknown {
  const y = toEchartsValue(d);
  const value = numeric === null ? y : [s.at?.[i] ?? numeric.values[i] ?? null, y];
  const label = s.pointLabels === undefined ? null : ctx.t(s.pointLabels.key, s.pointLabels.values[i] ?? {});
  const hollow = s.hollow?.[i] === true;
  if (label === null && !hollow) return value;
  return {
    value,
    ...(label === null ? {} : { name: label }),
    ...(label !== null && s.pointLabels?.drawn === true
      ? { label: { show: true, position: 'top', formatter: label, ...textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.annotation) } }
      : {}),
    ...(hollow ? { itemStyle: { color: ctx.theme.surface, borderColor: colour, borderWidth: ctx.theme.structure.lineWidth } } : {}),
  };
}

function categoryAxisFor(spec: ChartSpec, ctx: BuildContext) {
  return {
    type: 'category' as const,
    data: categoriesOf(spec),
    name: ctx.t(spec.categoryAxis.labelKey),
    ...X_AXIS_NAME,
    nameTextStyle: textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.axisLabel),
    // A week declared by its ISO start, or a run by its minute, is drawn as a date (audit 2, G).
    axisLabel: { ...textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.axisLabel), formatter: (value: string) => categoryWords(String(value), ctx.locale) },
    axisLine: { lineStyle: { color: ctx.theme.structure.axis, width: 1 } },
    axisTick: { show: false },
    splitLine: { show: false },
    // Inverse of ECharts' default for a time axis: we never re-sort, because the
    // declared order is the contract.
    inverse: false,
  };
}

/**
 * design/09 §7.4 — the declared category-axis annotation, drawn recessively.
 *
 * The caller supplies a position and a label key and nothing else (see `EventLine`);
 * every visual decision is made here, once, so a floor-set marker looks the same on
 * every chapter's chart and in both themes. The hairline grid token is the same
 * recession §7.4 gives the grid and the axis, the label wears the annotation ink token
 * rather than a series colour, and the line is `silent` so it never steals a tooltip
 * from the data it annotates.
 *
 * `validate.ts` has already asserted every position exists on the declared axis, so
 * nothing here can land at the origin.
 */
function eventLineMark(spec: ChartSpec, ctx: BuildContext, horizontal = false): EChartsOption | undefined {
  const lines = spec.eventLines;
  if (lines === undefined || lines.length === 0) return undefined;
  return {
    silent: true,
    symbol: ['none', 'none'],
    animation: false,
    lineStyle: { color: ctx.theme.structure.grid, width: 1, type: 'dashed' },
    emphasis: { disabled: true },
    label: {
      show: true,
      position: 'end',
      ...textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.annotation),
    },
    // On a horizontal bar the categories run down the y axis, so the marker is a y position.
    data: lines.map((line) => ({
      ...(horizontal ? { yAxis: line.at } : { xAxis: line.at }),
      name: ctx.t(line.labelKey),
      label: { formatter: ctx.t(line.labelKey) },
    })),
  };
}

function seriesColour(spec: ChartSpec, s: Series, ctx: BuildContext): string {
  const ids = spec.series.map((x) => x.id);
  const slot = slotForSeries(s, ids);
  const colour = ctx.theme.categorical[slot - 1];
  if (colour === undefined) throw new Error(`palette slot ${slot} does not exist — the categorical palette has 7 slots (§7.2)`);
  return colour;
}

/**
 * Whether a bar form was DECLARED horizontal. `orientation` was on `BarChartSpec` from the start
 * and nothing read it, so a `stacked-bar` asked for horizontally rendered vertically (ui-b,
 * 2026-09-26). Read here, once, for every bar form that declares it — the diverging bar too.
 */
export function isHorizontal(spec: ChartSpec): boolean {
  return 'orientation' in spec && spec.orientation === 'horizontal';
}

/** Room above the plot for the legend's one line (a scrolling legend never wraps). */
const LEGEND_ROOM = 28;

function buildCartesian(spec: ChartSpec, ctx: BuildContext, kind: 'line' | 'bar' | 'scatter'): EChartsOption {
  const { legend, directLabels } = legendPolicy(spec.series.length);
  /*
   * Room for the direct labels. A line with ≤4 series names each series at its END, which
   * is to the right of the plot — and `grid.right` was 24px, so the label was cut at the
   * canvas edge: audit v4 G-12's "Dema" was "Demand shape". `containLabel` measures tick
   * labels only, so the margin is sized here from the longest resolved series name.
   *
   * An ESTIMATE of the rendered width, not a measurement: 0.62 em per character is a
   * generous average for DM Sans at the label size, erring towards too much room, because
   * the failure on the other side is a name cut in half.
   */
  const endLabelRoom = directLabels && kind === 'line'
    ? Math.ceil(Math.max(...spec.series.map((s) => seriesName(ctx.t, s).length)) * ctx.theme.fontSize.label * 0.62) + 12
    : 0;
  const stacked = spec.form === 'stacked-bar';
  /*
   * §7.5 — the price rung is "a stepped line … never interpolated between steps".
   *
   * `'end'` and not `'start'` or `'middle'`: the value declared at week *i* is the rung
   * that was IN FORCE THROUGH week i, so the segment runs flat across week i and the
   * riser falls on the boundary where the price actually changed. `'middle'` would put
   * the riser in the middle of a week no price changed in, which is the same class of
   * invented timing that `smooth` is banned for.
   *
   * This is the only place `step` is written, and it is written from the FORM rather
   * than from a caller-supplied option — there is no `step` field on `ChartSpec` to set.
   */
  const stepped = spec.form === 'step-line';

  // ECharts hangs a markLine off a series, so it goes on the first one — it is a
  // property of the AXIS, not of that series, and it is drawn once either way.
  const numeric = spec.categoryAxis.kind === 'numeric' ? spec.categoryAxis : null;
  const horizontal = kind === 'bar' && numeric === null && isHorizontal(spec);
  // A numeric axis carries reference marks rather than event lines (validate.ts enforces it).
  const eventLines = numeric === null
    ? eventLineMark(spec, ctx, horizontal)
    : referenceMarks('references' in spec ? spec.references : undefined, numeric, spec, ctx);

  const series = spec.series.map((s, index) => {
    const colour = seriesColour(spec, s, ctx);
    const base = {
      id: s.id,
      ...(index === 0 && eventLines !== undefined ? { markLine: eventLines } : {}),
      name: seriesName(ctx.t, s),
      type: kind,
      // On a numeric axis each point is [x, y] — at its true x, never evenly spaced; a series
      // measured at its own positions (ADR 9050) at those.
      data: s.data.map((d, i) => pointOf(s, i, d, numeric, colour, ctx)),
      // §2.5 — series distinguishable by more than hue.
      symbol: markerSymbol[s.distinguisher] ?? 'circle',
      symbolSize: ctx.theme.structure.markerSize,
      itemStyle: {
        color: colour,
        // §7.4 — 2px surface gap between adjacent and stacked fills.
        ...(kind === 'bar'
          ? { borderColor: ctx.theme.surface, borderWidth: ctx.theme.structure.fillGap }
          : {}),
        ...(s.distinguisher === 'pattern-hatch' || s.distinguisher === 'pattern-dot'
          ? {
            decal: {
              symbol: s.distinguisher === 'pattern-dot' ? 'circle' : 'rect',
              dashArrayX: s.distinguisher === 'pattern-dot' ? [1, 4] : [1, 0],
              dashArrayY: s.distinguisher === 'pattern-dot' ? [2, 5] : [4, 3],
              rotation: Math.PI / 4,
              color: ctx.theme.surface,
            },
          }
          : {}),
      },
      // §7.4 — thin marks; 2px lines.
      ...(kind === 'line'
        ? {
          lineStyle: { color: colour, width: ctx.theme.structure.lineWidth },
          ...(stepped ? { step: 'end' as const } : {}),
          // §7.1 `area` — cumulative magnitude, filled down to the axis. No existing screen draws
          // an area (checked 2026-09-26), so this fill changes nothing already rendered.
          ...(spec.form === 'area' ? { areaStyle: { color: colour, opacity: 0.16 } } : {}),
        }
        : {}),
      // A gap stays a gap.
      connectNulls: false,
      // A horizontal bar is a band, not a slab: one bar (a pipeline of states) stays a strip.
      ...(horizontal ? { barMaxWidth: 40 } : {}),
      ...(stacked ? { stack: 'total' } : {}),
      // §2.5 — "No series labels where a value axis is present; direct labels for
      // ≤4 series". The direct label names the SERIES at its end, not every point.
      endLabel: directLabels && kind === 'line'
        ? { show: true, ...textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.annotation), formatter: seriesName(ctx.t, s) }
        : { show: false },
      label: { show: false },
    };
    return base;
  });
  const trend = [...trendSeries(spec, ctx), ...intervalSeries(spec, ctx)];
  // A line whose series sit at their own positions is read point by point (ADR 9050): an axis
  // crosshair would list every series at an x most of them were never evaluated at.
  const perItem = kind !== 'line' || spec.series.some((s) => s.at !== undefined);
  // The crosshair's read-out and the tooltip's header carry the category: a date, not its ISO code.
  const pointerLabel = {
    formatter: (params: { readonly value?: unknown }) => (typeof params.value === 'string'
      ? categoryWords(params.value, ctx.locale)
      : ctx.formatNumber(Number(params.value))),
  };

  return {
    // Transparent on screen: the chart sits on whatever its container paints. Painting
    // `surface-page` here drew an off-white rectangle inside every white panel — the same
    // rectangle audit v4 G-26 saw, much louder, on a dark page. An EXPORTED image still
    // gets an opaque surface: `toImageBlob` is passed one explicitly, because a PNG with
    // no background is unreadable wherever it is pasted.
    backgroundColor: 'transparent',
    animation: false,
    grid: {
      left: 56,
      right: Math.max(24, endLabelRoom),
      top: 16 + NAME_ROOM.top + (legend ? LEGEND_ROOM : 0),
      bottom: 40 + NAME_ROOM.bottom,
      containLabel: true,
    },
    // ONE value axis. This is an object, never an array — a second y-axis has
    // nowhere to go. G2 §2.5, design/09 §12.4. A HORIZONTAL bar swaps which side each
    // axis is drawn on; it is still one value axis and one category axis.
    ...(horizontal
      ? {
        xAxis: {
          ...valueAxisFor(spec, ctx),
          ...X_AXIS_NAME,
          nameTextStyle: textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.axisLabel),
        },
        yAxis: {
          ...categoryAxisFor(spec, ctx),
          // The declared order reads top to bottom, as a list does; the name sits above it.
          inverse: true,
          nameLocation: 'start' as const,
          nameGap: 12,
          nameTextStyle: { ...textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.axisLabel), align: 'left' as const },
        },
      }
      : {
        yAxis: valueAxisFor(spec, ctx),
        xAxis: numeric === null ? categoryAxisFor(spec, ctx) : numericAxisFor(numeric, ctx),
      }),
    series: [...series, ...trend],
    /*
     * The legend sits ABOVE the plot, on one scrolling line. It was at `bottom: 0`, where a
     * legend of four or more series wrapped upwards into the category axis's labels and name
     * (ui-b, 2026-09-26): its height depends on the width and the words, and no fixed
     * `grid.bottom` can be right for both. One scrolling line has a known height, so the
     * plot is moved down by exactly that and nothing can overlap, at any width.
     */
    legend: legend
      ? {
        show: true, type: 'scroll', top: 0, left: 0, icon: 'roundRect',
        // The series only: a trend path is named at its end, not in the legend (ADR 9050).
        ...(trend.length > 0 ? { data: series.map((one) => one.name) } : {}),
        textStyle: textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.annotation),
        pageTextStyle: textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.annotation),
        pageIconColor: ctx.theme.ink.annotation,
        pageIconInactiveColor: ctx.theme.structure.grid,
      }
      : { show: false },
    tooltip: {
      // §7.4 — crosshair + tooltip on line/area, per-mark tooltip on bar/dot/cell.
      trigger: perItem ? 'item' : 'axis',
      // A speaking tooltip already names the category and the value, so the crosshair's axis
      // read-outs ("0.59" on a count axis) would only repeat it less precisely: the column is
      // shaded instead.
      axisPointer: spec.tooltipKey !== undefined && perItem
        ? { type: 'shadow', label: pointerLabel }
        : { type: 'cross', lineStyle: { color: ctx.theme.structure.axis }, label: pointerLabel },
      textStyle: textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.title),
      // The chart's own surface and rule, not ECharts' white box: a white panel on the dark
      // theme was the one thing on a dark page still painted for a light one.
      backgroundColor: ctx.theme.surface,
      borderColor: ctx.theme.structure.axis,
      valueFormatter: (v: unknown) => (v === null || v === undefined
        // The tooltip says which kind of nothing it is, too.
        ? ctx.t('chart.missing.noObservation')
        : ctx.formatNumber(Number(v))),
      ...(spec.tooltipKey !== undefined && perItem
        ? { formatter: speakingTooltip(spec.tooltipKey, ctx) }
        : perItem && categoriesOf(spec).some(isDateLabel)
          ? { formatter: datedItemTooltip(ctx) }
          : {}),
    },
    ...(spec.valueAxis.targetBand
      ? {
        // §7.5 — weeks-of-cover distribution is "a histogram with a target band
        // annotation. Not a gauge."
        graphic: [],
      }
      : {}),
  };
}

function buildHeatmap(spec: Extract<ChartSpec, { form: 'heatmap' }>, ctx: BuildContext): EChartsOption {
  const cols = categoriesOf(spec);
  const rowAxis = spec.rowAxis;
  const rows = rowAxis.kind === 'size-scale' || rowAxis.kind === 'ordinal'
    ? rowAxis.order
    : spec.series.map((s) => seriesName(ctx.t, s));

  const data: [number, number, number | null][] = [];
  spec.series.forEach((s, r) => {
    s.data.forEach((d, c) => data.push([c, r, toEchartsValue(d)]));
  });

  const ramp = ctx.theme.sequential(spec.ramp);
  const heatmapEventLines = eventLineMark(spec, ctx);
  return {
    // Transparent on screen: the chart sits on whatever its container paints. Painting
    // `surface-page` here drew an off-white rectangle inside every white panel — the same
    // rectangle audit v4 G-26 saw, much louder, on a dark page. An EXPORTED image still
    // gets an opaque surface: `toImageBlob` is passed one explicitly, because a PNG with
    // no background is unreadable wherever it is pasted.
    backgroundColor: 'transparent',
    animation: false,
    grid: { left: 96, right: 24, top: 16 + NAME_ROOM.top, bottom: 56 + NAME_ROOM.bottom, containLabel: true },
    xAxis: { ...categoryAxisFor(spec, ctx), data: cols },
    yAxis: {
      type: 'category', data: rows,
      name: ctx.t(rowAxis.labelKey),
      ...Y_AXIS_NAME,
      nameTextStyle: { ...textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.axisLabel), align: 'left' as const },
      axisLabel: textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.axisLabel),
      axisLine: { lineStyle: { color: ctx.theme.structure.axis } },
      axisTick: { show: false }, splitLine: { show: false },
    },
    visualMap: {
      min: 0, max: Math.max(1, maxAbsolute(spec.series)),
      calculable: false, orient: 'horizontal', left: 'center', bottom: 0,
      // §7.3 — single blue hue, light→dark. Never a rainbow.
      inRange: { color: ramp as string[] },
      textStyle: textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.annotation),
      text: [ctx.t('chart.legend.high'), ctx.t('chart.legend.low')],
    },
    series: [{
      type: 'heatmap',
      data,
      ...(heatmapEventLines === undefined ? {} : { markLine: heatmapEventLines }),
      // A broken size run reads as a GAP, which §7.5 calls "the single most legible
      // view" of the problem. An absent cell is drawn in the missing token, not in
      // the lightest ramp step, so "not ranged" never looks like "sold nothing".
      itemStyle: { borderColor: ctx.theme.surface, borderWidth: ctx.theme.structure.fillGap },
      label: { show: false },
      emphasis: { itemStyle: { borderColor: ctx.theme.ink.title, borderWidth: 1 } },
    }],
    tooltip: {
      trigger: 'item',
      textStyle: textStyle(ctx, ctx.theme.fontSize.label, ctx.theme.ink.title),
      valueFormatter: (v: unknown) => (v === null || v === undefined
        ? ctx.t('chart.missing.noObservation') : ctx.formatNumber(Number(v))),
    },
    legend: { show: false },
  };
}

function buildDiverging(spec: Extract<ChartSpec, { form: 'diverging-bar' }>, ctx: BuildContext): EChartsOption {
  const palette = ctx.theme.diverging;
  const mid = Math.floor(palette.length / 2);
  const values = spec.series.flatMap((s) => s.data.filter((d): d is number => !isMissing(d)));
  const span = Math.max(1, ...values.map((v) => Math.abs(v - spec.neutralAt)));

  const colourFor = (value: number): string => {
    const offset = value - spec.neutralAt;
    // §7.3 — equal steps per arm; the red arm means "below plan", consistently
    // across every chart in the product.
    //
    // The neutral midpoint is for a value AT the neutral point and nothing else. It is a warm
    // grey a step off the surface, so a bar drawn in it is a bar nobody can see: with a skewed
    // spread (one article at 66M COP, three hundred between 1M and 10M) rounding put every
    // small bar on step 0 and the chart drew no bars at all (issue 215). A value off the neutral
    // point takes at least the first step of its own arm.
    const step = offset === 0 ? 0 : Math.max(1, Math.min(mid, Math.round((Math.abs(offset) / span) * mid)));
    const index = offset < 0 ? mid - step : mid + step;
    const colour = palette[index] ?? palette[mid];
    if (colour === undefined) throw new Error('diverging palette is empty');
    return colour;
  };

  const base = buildCartesian(spec, ctx, 'bar');
  const horizontal = isHorizontal(spec);
  const eventLines = eventLineMark(spec, ctx, horizontal);
  return {
    ...base,
    series: spec.series.map((s, index) => ({
      id: s.id,
      name: seriesName(ctx.t, s),
      type: 'bar',
      // The series are rebuilt here rather than reused, so the axis annotation has to
      // be re-attached — otherwise a diverging chart silently loses its markers.
      ...(index === 0 && eventLines !== undefined ? { markLine: eventLines } : {}),
      // No surface border (§7.4's fill gap). The gap separates ADJACENT and STACKED fills, and a
      // diverging bar is one bar per category, already apart by the category gap. The border is
      // stroked on the bar's edge, so on a dense axis — 304 articles, a bar about 3px wide — the
      // 2px surface stroke painted over the whole fill and the bars vanished (issue 215).
      data: s.data.map((d) => (isMissing(d)
        ? null
        : { value: d, itemStyle: { color: colourFor(d) } })),
      connectNulls: false,
      ...(horizontal ? { barMaxWidth: 40 } : {}),
      label: { show: false },
    })),
    // A diverging chart's zero line is the reading anchor, so it is drawn rather
    // than left to the grid.
    markLine: undefined,
  };
}

/** Builds the private ECharts option. Never exported outside src/charts/. */
export function buildOption(spec: ChartSpec, ctx: BuildContext): EChartsOption {
  switch (spec.form) {
    case 'line': return buildCartesian(spec, ctx, 'line');
    // §7.5 — the price rung. Same cartesian build; `step: 'end'` is set from the form.
    case 'step-line': return buildCartesian(spec, ctx, 'line');
    case 'area': return buildCartesian(spec, ctx, 'line');
    case 'bar':
    case 'clustered-bar':
    case 'stacked-bar':
    case 'paired-bar':
    case 'histogram': return buildCartesian(spec, ctx, 'bar');
    case 'scatter': return buildCartesian(spec, ctx, 'scatter');
    case 'heatmap': return buildHeatmap(spec, ctx);
    case 'diverging-bar': return buildDiverging(spec, ctx);
    case 'small-multiples': {
      // One panel per entry — §7.5: "Never seven overlaid lines." Each panel is an
      // independent chart instance rendered by <SmallMultiples>, so there is no
      // shared-axis trick that could become a dual axis.
      throw new Error('small-multiples is composed of independent charts by <SmallMultiples>, not built as one option');
    }
    default: {
      const exhaustive: never = spec;
      throw new Error(`unhandled chart form: ${JSON.stringify(exhaustive)}`);
    }
  }
}

/**
 * Both renderers are registered, and the choice is made by capability rather than by
 * configuration.
 *
 * Canvas is the default because it is what holds a frame budget on a screen with several
 * charts. But a 2D context is not always available: some managed desktop builds disable
 * canvas, and jsdom has no implementation at all — which is where the chart tests run. In
 * either case an SVG chart is correct and readable, and a blank rectangle is not, so the
 * renderer degrades rather than the chart failing.
 *
 * The capability probe is cached: it costs a canvas allocation and the answer cannot change
 * within a session.
 */
let canvasAvailable: boolean | null = null;

function hasCanvas2d(): boolean {
  if (canvasAvailable !== null) return canvasAvailable;
  try {
    canvasAvailable = document.createElement('canvas').getContext('2d') !== null;
  } catch {
    canvasAvailable = false;
  }
  return canvasAvailable;
}

export function createInstance(container: HTMLElement, renderer?: 'canvas' | 'svg'): EChartsInstance {
  const chosen = renderer ?? (hasCanvas2d() ? 'canvas' : 'svg');
  return echarts.init(container, undefined, {
    renderer: chosen,
    // Only meaningful for canvas, and only a gain there: it limits repaint to the region
    // that changed, which is most of the scroll-frame budget on a dashboard of charts.
    useDirtyRect: chosen === 'canvas',
  });
}

/**
 * The chart's motion — the designer's audit 9.1: "`Chart` adds the growth animation of the
 * first draw (ECharts: `animationDuration` from the token)" (`docs/adr/9029`).
 *
 * Without this ECharts animated EVERY draw at its own default (1000ms, `cubicOut`) and
 * ignored `prefers-reduced-motion`, because the library draws on a canvas the stylesheet's
 * reduced-motion rule cannot reach. Now the first draw of a plot grows over `motion.chart`,
 * and every later draw of the same plot (a theme switch, a new spec), and every draw for a
 * person who asked for reduced motion, is instant. `cubicOut` is the library's named curve
 * nearest the `motion.easing-out` token: ECharts takes a name or a function, not a CSS
 * `cubic-bezier()`.
 */
export function chartMotion(animate: boolean): EChartsOption {
  if (!animate) return { animation: false };
  return {
    animation: true,
    animationDuration: motionMs('motion-chart'),
    animationEasing: 'cubicOut',
    animationDurationUpdate: 0,
  };
}

export function applyOption(instance: EChartsInstance, option: EChartsOption): void {
  // `notMerge: true` — a re-render replaces the option wholesale. Merging is how a
  // stale `yAxis` array survives a spec change and a chart quietly grows a second
  // axis that no spec describes.
  instance.setOption(option, { notMerge: true, lazyUpdate: false });
}

export function disposeInstance(instance: EChartsInstance): void {
  instance.dispose();
}

/** G2.4 — "Charts additionally export as PNG and SVG." */
export async function toImageBlob(instance: EChartsInstance, format: 'png' | 'svg', backgroundColor: string): Promise<Blob> {
  if (format === 'svg') {
    const dataUrl = instance.getDataURL({ type: 'svg', backgroundColor });
    const svg = decodeURIComponent(dataUrl.replace(/^data:image\/svg\+xml[^,]*,/, ''));
    return new Blob([svg], { type: 'image/svg+xml' });
  }
  const dataUrl = instance.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor });
  const response = await fetch(dataUrl);
  return response.blob();
}
